﻿import { FunctionComponent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact, AgGridReactProps } from "@ag-grid-community/react";
import { EntityCandidate } from "../../../../api/models/EntityCandidate";
import { Badge, Box, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { CandidateStatusCellRenderer } from "./CandidateStatusCellRenderer";
import CandidateStatusFilter from "./CandidateStatusFilter";
import EmploymentStatusFilter from "./EmploymentStatusFilter";
import DesignatedZoneFilter from "./DesignatedZoneFilter";
import { useNavigate } from "react-router-dom";
import { CandidateGridMenuBar } from "./CandidateGridMenuBar";
import { ImportCandidatesModal } from "../ImportCandidatesModal";
import { ColDef, ICellRendererParams, IGetRowsParams, ModuleRegistry, RowClickedEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { InfiniteRowModelModule } from "@ag-grid-community/infinite-row-model";

ModuleRegistry.registerModules([InfiniteRowModelModule]);
import { AgGridContainer, Shimmer } from "@am-tax/fe-core";

import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import "../../../../assets/css/ag-theme.css";
import "../../../../assets/css/ag-grid-react-custom.css";
import { useAreaPermissions } from "../../../../hooks";
import { AreaPermissions, CandidatesArea } from "../../../../auth";
import { useUserContext } from "../../../../context";
import { axiosInstance, Constants, sanitizeForLog, validateUuid } from "../../../../utils";
import { CheckboxHeaderComponent } from "../../../../components/AgGridReact/CheckboxHeaderComponent";
import agGridDateFormatter from "../../../../grid/formatters/agGridDateFormatter";
import QualificationFilter from "./QualificationFilter";
import { getEntityCandidates, getEntityCandidatesCount } from "../../../../api/getEntityCandidates";
import { ObjectHelper } from "../../../../utils/objectHelper";
import { EntityResponse } from "../../../../api";
import { UseQueryResult } from "@tanstack/react-query";
import { useTenant } from "../../../../api/api";
import { CandidateFieldsConfiguration } from "../../../../api/models/TenantResponse";

const candidateGridFilterModelKey = "candidateGridFilterModel";
const candidateGridSortModelKey = "candidateGridSortModel";

export interface CandidateGridProps {
    before?: ReactNode;
    clientId: string;
    entityId: string | undefined;
    entitiesQuery: UseQueryResult<EntityResponse[], unknown>;
}

function getFieldNumber(index: number) {
    return index.toString().padStart(2, "0");
}

function getCustomFieldDisplayName(config: CandidateFieldsConfiguration, fieldNumber: string) {
    return (ObjectHelper.get(config, `customField${fieldNumber}DisplayName`) as string) || "";
}

function getDynamicColumnName(fieldNumber: string) {
    return `candidateCustomFields.customField${fieldNumber}`;
}

function isCustomFieldShownInGrid(config: CandidateFieldsConfiguration, fieldNumber: string) {
    return ObjectHelper.get(config, `isCustomField${fieldNumber}ShownInGrid`);
}

function getDynamicColumns(candidateFieldsConfiguration: CandidateFieldsConfiguration | null | undefined): Array<ColDef<EntityCandidate>> {
    if (!candidateFieldsConfiguration) {
        return [];
    }

    const newDynamicColumns: Array<ColDef<EntityCandidate>> = [];

    for (let index = 1; index <= 5; index++) {
        const fieldNumber = getFieldNumber(index);

        if (isCustomFieldShownInGrid(candidateFieldsConfiguration, fieldNumber)) {
            const customFieldDisplayName = getCustomFieldDisplayName(candidateFieldsConfiguration, fieldNumber);
            const columnName = getDynamicColumnName(fieldNumber);

            if (!newDynamicColumns.some(col => col.field === columnName)) {
                newDynamicColumns.push({
                    headerName: customFieldDisplayName,
                    field: columnName,
                    minWidth: 80,
                    filter: true,
                    sortable: true,
                } as ColDef<EntityCandidate>);
            }
        }
    }

    return newDynamicColumns;
}

function getColumnDefs(
    areaPermissions: AreaPermissions<typeof CandidatesArea>,
    fullReadColumns: Array<ColDef<EntityCandidate>>,
    candidateFieldsConfiguration: CandidateFieldsConfiguration | null | undefined,
    dateFormat: string
): Array<ColDef<EntityCandidate>> {
    return [
        {
            headerName: "",
            headerComponent: CheckboxHeaderComponent,
            checkboxSelection: true,
            width: 52,
            pinned: "left",
            lockPosition: true,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMenu: true,
            colId: "checkboxCol",
            suppressClickEdit: true,
            suppressHeaderKeyboardEvent: true,
        },
        {
            headerName: "First",
            field: "firstName",
            minWidth: 80,
            filter: true,
            sortable: true,
        },
        {
            headerName: "Last",
            field: "lastName",
            minWidth: 80,
            filter: true,
            sortable: true,
        },
        ...getDynamicColumns(candidateFieldsConfiguration),
        {
            field: "designatedZoneType",
            headerName: "Designated Zone Type",
            filter: DesignatedZoneFilter,
        },
        {
            field: "status",
            headerName: "Status",
            cellRenderer: CandidateStatusCellRenderer,
            filter: CandidateStatusFilter,
            sortable: true,
        },
        ...(areaPermissions.has(CandidatesArea.read_full) ? fullReadColumns : []),
        {
            field: "qualifications",
            headerName: "Qualifications",
            cellRenderer: ({ data }: ICellRendererParams) => {
                return data?.qualifications.map((q: any) => q).join(", ");
            },
            filter: QualificationFilter,
            sortable: true,
        },
        {
            field: "employmentStatus",
            headerName: "Employment Status",
            cellRenderer: ({ data }: ICellRendererParams) => {
                return data?.employmentStatus;
            },
            filter: EmploymentStatusFilter,
            sortable: true,
        },
        {
            field: "daysRemaining",
            headerName: "Days Remaining",
            cellRenderer: ({ data }: ICellRendererParams) =>
                data?.daysRemaining !== null &&
                data?.daysRemaining >= 0 && (
                    <Box>
                        <Badge
                            colorScheme={data?.daysRemaining > 21 ? "green" : data?.daysRemaining > 14 ? "yellow" : "red"}
                            variant={"solid"}
                            lineHeight={"var(--chakra-lineHeights-base)"}
                        >
                            {data?.daysRemaining}
                        </Badge>
                    </Box>
                ),
            filter: "agNumberColumnFilter",
            sortable: true,
        },
        {
            field: "questionnaireCompletedOn",
            headerName: "Completed On",
            cellRenderer: ({ data }: ICellRendererParams) => (
                <>
                    <Text>{agGridDateFormatter(data?.questionnaireCompletedOn, dateFormat)}</Text>
                </>
            ),
            filter: "agDateColumnFilter",
            sortable: true,
        },
        {
            field: "employmentHireDate",
            headerName: "Hire Date",
            cellRenderer: ({ data }: ICellRendererParams) => (
                <>
                    <Text>{agGridDateFormatter(data?.employmentHireDate, dateFormat)}</Text>
                </>
            ),
            filter: "agDateColumnFilter",
            sortable: true,
        },
        {
            field: "employmentStartDate",
            headerName: "Start Date",
            cellRenderer: ({ data }: ICellRendererParams) => (
                <>
                    <Text>{agGridDateFormatter(data?.employmentStartDate, dateFormat)}</Text>
                </>
            ),
            filter: "agDateColumnFilter",
            sortable: true,
        },
    ] as Array<ColDef<EntityCandidate>>;
}

export const CandidateGrid: FunctionComponent<CandidateGridProps> = ({ before, clientId, entityId, entitiesQuery }) => {
    const navigate = useNavigate();
    const { dateFormat } = useUserContext();
    const [candidateCount, setCandidateCount] = useState<number>(0);
    const areaPermissions = useAreaPermissions(CandidatesArea, clientId);
    const [lastEntityId, setLastEntityId] = useState<string | undefined>(entityId);
    const importModal = useDisclosure();
    const tenantQuery = useTenant(clientId);

    const fullReadColumns: Array<ColDef<EntityCandidate>> = [
        {
            field: "selectedQualification",
            headerName: "Target Qualification",
            minWidth: 120,
            filter: false,
            sortable: true,
        },
        // NOTE: Hide because: Task 34111: Hide the "Follow Up" Column
        // {
        //     headerName: "Follow-up",
        //     field: "openRequestCount",
        //     cellRenderer: ({ data }: ICellRendererParams) =>
        //         !!data?.openRequestCount && (
        //             <Box>
        //                 <Badge colorScheme={"red"} lineHeight={"var(--chakra-lineHeights-base)"}>
        //                     {data?.openRequestCount} Open Requests
        //                 </Badge>
        //             </Box>
        //         ),
        //     minWidth: 180,
        //     filter: "agNumberColumnFilter",
        // },
    ];

    const [columnDefs, setColumnDefs] = useState(getColumnDefs(areaPermissions, fullReadColumns, tenantQuery.data?.candidateFieldsConfiguration, dateFormat));

    const getSavedFilterModel = () => {
        const savedFilterModel = sessionStorage.getItem(candidateGridFilterModelKey);
        return savedFilterModel ? JSON.parse(savedFilterModel) : {};
    };

    const getSavedSortModel = () => {
        const savedSortModel = sessionStorage.getItem(candidateGridSortModelKey);
        return savedSortModel ? JSON.parse(savedSortModel) : [];
    };

    const [sortModel, setSortModel] = useState<any[]>(() => {
        return getSavedSortModel();
    });

    const [filterModel, setFilterModel] = useState<any>(() => {
        return getSavedFilterModel();
    });
    const [gridReady, setGridReady] = useState(false);

    const gridRef = useRef<AgGridReact<EntityCandidate>>(null);

    // Memoize the datasource so it is only recreated when clientId or entityId changes.
    // Without this, every React state update (gridReady, filterModel, sortModel, candidateCount)
    // creates a new datasource reference, causing AG Grid to purge its cache and re-fetch on every render.
    const datasource = useMemo(
        () => ({
            getRows: (filters: IGetRowsParams) => {
                gridRef.current?.api.showLoadingOverlay();
                const { qualifications, ...restFilterModel } = filters.filterModel || {};
                filters.filterModel = restFilterModel;
                const qualificationFilter = qualifications?.values?.join(",") || Constants.emptyString;

                axiosInstance()
                    .get<{ rowsThisBlock: EntityCandidate[]; lastRow?: number }>(
                        `api/Clients/${validateUuid(clientId)}/Entities/${validateUuid(entityId) ?? "all"}/candidates/grid`,
                        {
                            params: {
                                query: JSON.stringify(filters),
                                qualificationFilter: qualificationFilter,
                            },
                        }
                    )
                    .then(async (response: any) => {
                        filters.successCallback(response.data.rowsThisBlock, response.data.lastRow);

                        setTimeout(() => {
                            gridRef.current?.api.hideOverlay();
                        }, 300);

                        // Read params directly from gridRef to avoid stale closure
                        const filterModel = gridRef.current?.api.getFilterModel();
                        const columnState = gridRef.current?.columnApi.getColumnState() || [];
                        const sortModel = columnState
                            .filter(col => col.sort && col.sortIndex !== null)
                            .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
                            .map(col => ({ sort: col.sort, colId: col.colId }));
                        const countParams = {
                            startRow: 0,
                            endRow: Constants.maxAllowedInt32,
                            filterModel,
                            sortModel,
                        } as IGetRowsParams;
                        const count = await getEntityCandidatesCount(clientId, entityId, countParams);
                        setCandidateCount(count);
                    })
                    .catch(error => {
                        console.error("Error loading grid data:", sanitizeForLog(error));
                        filters.failCallback();
                        gridRef.current?.api.hideOverlay();
                    });
            },
        }),
        [clientId, entityId]
    );

    const candidatesGrid: AgGridReactProps<EntityCandidate> = {
        rowModelType: "infinite",
        defaultColDef: {
            flex: 1,
            filter: false,
            sortable: false,
            resizable: true,
        },
        columnDefs,
        onRowClicked(event: RowClickedEvent<EntityCandidate>) {
            // Check if the click was on a checkbox or in the checkbox column
            const target = event.event?.target as HTMLElement;

            // Check if the click was on a checkbox or its container
            if (
                target &&
                (target.classList.contains("ag-checkbox-input") ||
                    target.classList.contains("ag-checkbox-wrapper") ||
                    target.closest(".ag-checkbox-input") ||
                    target.closest(".ag-checkbox-wrapper") ||
                    // Also check for the checkbox column cell
                    target.closest('.ag-cell-value[col-id="checkboxCol"]') ||
                    target.closest('.ag-cell[col-id="checkboxCol"]'))
            ) {
                // If it's a checkbox click, prevent navigation
                return;
            }

            navigate(`/clients/${clientId}/candidates/${event.data?.entityId}/${event.data?.id}`);
        },
        getRowStyle: () => ({
            cursor: "pointer",
        }),
        onSortChanged: () => {
            saveCurrentSorting();
        },
        onFilterChanged: () => {
            saveCurrentFilters();
        },
        onGridReady: () => {
            setTimeout(() => {
                setGridReady(true);
            }, 100); // Set a timeout to ensure the Shimmer is shown before Data grid is ready
        },
        rowSelection: "multiple",
        suppressRowClickSelection: true,
        suppressAnimationFrame: false,
        suppressPropertyNamesCheck: true,
        suppressCellFocus: true,
        suppressRowHoverHighlight: false,
        enableCellTextSelection: true,
        cacheBlockSize: 100,
        maxBlocksInCache: 10,
        getRowId: params => {
            return params.data.id;
        },
    };

    const saveCurrentSorting = () => {
        const columnState = gridRef.current?.columnApi.getColumnState();
        const colStateJson = JSON.stringify(columnState);
        const sortModelJson = JSON.stringify(sortModel);

        if (colStateJson !== sortModelJson) {
            setTimeout(() => {
                const columnState = gridRef.current?.columnApi.getColumnState();
                const colStateJson = JSON.stringify(columnState);
                setSortModel(columnState || []);
                sessionStorage.setItem(candidateGridSortModelKey, colStateJson);
            }, 0);
        }
    };

    const saveCurrentFilters = () => {
        const newFilterModel = gridRef.current?.api.getFilterModel();
        const newFilterModelJson = JSON.stringify(newFilterModel);
        const filterModelJson = JSON.stringify(filterModel);

        if (newFilterModelJson !== filterModelJson) {
            setTimeout(() => {
                const newFilterModel = gridRef.current?.api.getFilterModel();
                const newFilterModelJson = JSON.stringify(newFilterModel);
                setFilterModel(newFilterModel || {});
                sessionStorage.setItem(candidateGridFilterModelKey, newFilterModelJson);
            }, 0);
        }
    };

    const clearFilterAndSortModels = () => {
        if (gridReady && gridRef.current) {
            gridRef.current.api.setFilterModel(null);
            gridRef.current.columnApi.resetColumnState();
            setSortModel([]);
            setFilterModel({});
            sessionStorage.removeItem(candidateGridSortModelKey);
            sessionStorage.removeItem(candidateGridFilterModelKey);
        }
    };

    const getEntityCandidateParams = () => {
        // Get current filter and sort models from the grid
        const filterModel = gridRef.current?.api.getFilterModel();
        // Get the sort model from the column state
        const columnState = gridRef.current?.columnApi.getColumnState() || [];
        const sortModel = columnState
            .filter(col => col.sort && col.sortIndex !== null)
            .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
            .map(col => ({
                sort: col.sort,
                colId: col.colId,
            }));
        // Define the row range you want to export (e.g., first 10,000 rows)
        const startRow = 0;
        const endRow = Constants.maxAllowedInt32;

        // Build the params object for useEntityCandidates
        const params = {
            startRow,
            endRow,
            filterModel,
            sortModel,
        } as IGetRowsParams;

        return params;
    };

    // Apply saved filter/sort state exactly once when the grid becomes ready.
    // The 100ms delay in onGridReady ensures sortModel and filterModel are already
    // populated from sessionStorage (via useState lazy init) before this runs.
    // Only apply when there is something to restore to avoid unnecessary cache purges.
    // Keeping sortModel/filterModel in deps would cause the effect to re-run every time
    // the user sorts/filters, calling api.setFilterModel() again and triggering extra fetches.
    useEffect(() => {
        if (gridReady && gridRef.current) {
            if (sortModel.length > 0) {
                gridRef.current.columnApi?.applyColumnState({ state: sortModel, applyOrder: true });
            }
            if (Object.keys(filterModel).length > 0) {
                gridRef.current.api?.setFilterModel(filterModel);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridReady]); // intentionally omit sortModel/filterModel — only restore once on grid ready

    useEffect(() => {
        if (entityId && lastEntityId === entityId) return;

        setLastEntityId(entityId);
        setColumnDefs(getColumnDefs(areaPermissions, fullReadColumns, tenantQuery.data?.candidateFieldsConfiguration, dateFormat));
    }, [entityId, tenantQuery.data?.candidateFieldsConfiguration]);

    // Check the number of selected checkboxes
    const [deleteIsDisabled, setDeleteIsDisabled] = useState(true);
    const onRowSelected = useCallback((event: RowSelectedEvent) => {
        const selectedNodesCount = event.api.getSelectedNodes().length;
        if (selectedNodesCount > 0) {
            setDeleteIsDisabled(false);
        } else {
            setDeleteIsDisabled(true);
        }
    }, []);

    const onExportCurrentGridViewToCsv = useCallback(async () => {
        if (gridRef.current && gridRef.current.api) {
            const cache = gridRef.current.api.getCacheBlockState();

            if (!cache) {
                console.warn("No data available in the grid to export.");
                return;
            }

            const params = getEntityCandidateParams();
            const rowData = await getEntityCandidates(clientId, entityId, params);

            // Get column headers as displayed on the screen
            const displayedColumns = gridRef.current.columnApi.getAllDisplayedColumns();
            const headers = displayedColumns.map(col => col.getColDef().headerName || col.getColDef().field);

            if (rowData.length === 0) {
                console.warn("No data available in the grid to export.");
                return;
            }

            // Map row data to match displayed columns
            const csvRows = rowData.map(row =>
                displayedColumns.map(col => {
                    const field = col.getColDef().field;
                    return field ? ObjectHelper.get(row, field) : ""; // Use field value or empty string if no field
                })
            );

            // Generate CSV content
            const validColumns = headers
                .map((header, index) => ({ header, index })) // Map headers to objects containing their value and index
                .filter(({ header }) => !!header); // Filter out headers that are not truthy

            const filteredHeaders = validColumns.map(({ header }) => header); // Extract the valid headers
            const filteredRows = csvRows.map(row => validColumns.map(({ index }) => row[index])); // Use the valid indices to filter the rows

            const csvContent = [
                filteredHeaders.join(","), // Headers
                ...filteredRows.map(row => row.join(",")), // Rows
            ].join("\n");

            // Trigger download without appending to DOM to avoid XSS vector
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.setAttribute("download", "Candidates.csv");
            link.dispatchEvent(new MouseEvent("click", { bubbles: false, cancelable: true, view: window }));
            URL.revokeObjectURL(url);

            console.log("CSV export successful.");
        } else {
            console.error("Grid reference is not initialized.");
        }
    }, [entityId, clientId]);

    const refreshDataGrid = () => {
        if (gridRef.current) {
            // First, clear all selections
            gridRef.current.api.deselectAll();

            // Then purge the cache to refresh the data
            gridRef.current.api.purgeInfiniteCache();
        }
    };

    return (
        <Shimmer isLoading={!gridReady}>
            <AgGridContainer
                gap={0}
                borderTop={"none"}
                borderRadius={0}
                marginBottom={-6}
                paddingBottom={0}
                before={
                    <Box>
                        {before}
                        <CandidateGridMenuBar
                            grid={gridRef.current}
                            clientId={clientId}
                            entityId={entityId}
                            onImportModalOpen={importModal.onOpen}
                            clearFilterAndSortModels={clearFilterAndSortModels}
                            refreshDataGrid={refreshDataGrid}
                            deleteIsDisabled={deleteIsDisabled}
                            onExportCurrentGridViewToCsv={onExportCurrentGridViewToCsv}
                        />
                    </Box>
                }
            >
                {/* 
                HACK: removing multiSortKey={"ctrl"} the AgGridReact only allows sorting by one column at a time.
                This is a bug in the AgGridReact library. The multiSortKey prop is not working as expected. 
                <AgGridReact {...candidatesGrid} ref={gridRef} rowSelection={"multiple"} multiSortKey={"ctrl"} onSelectionChanged={onRowSelected} /> 
                */}
                <AgGridReact {...candidatesGrid} datasource={datasource} ref={gridRef} rowSelection={"multiple"} onSelectionChanged={onRowSelected} />
                {entityId && <ImportCandidatesModal {...importModal} clientId={clientId} entityId={entityId} />}
            </AgGridContainer>
            <Flex justify="flex-end" pb={4}>
                <Text fontSize="sm" color="black.500">
                    Candidate Count: <strong>{candidateCount}&nbsp;</strong>
                </Text>
            </Flex>
        </Shimmer>
    );
};
